
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CreateRecipeForm } from "@/components/recipe/CreateRecipeForm";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { RecipeInput } from "@/components/RecipeInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConversionResult } from "@/types/recipe";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const CreateRecipe = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [recipe, setRecipe] = useState<ConversionResult | null>(null);
  const [airfryerMode, setAirfryerMode] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to create a recipe");
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  const handleRecipeImport = async (input: string, type: "url" | "text") => {
    console.log('Starting recipe import with:', { type, inputLength: input.length, airfryerMode });
    setIsLoading(true);
    setRecipe(null);
    setLastError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      const temperatureUnit = localStorage.getItem("temperatureUnit") || "F";

      console.log('Preparing to call convert-recipe function with:', {
        type,
        userId,
        airfryerMode,
        temperatureUnit
      });

      // Use supabase.functions.invoke directly instead of trying fetch first
      try {
        const { data, error } = await supabase.functions.invoke('convert-recipe', {
          body: {
            url: type === "url" ? input : null,
            text: type === "text" ? input : null,
            userId,
            airfryerMode,
            temperatureUnit
          },
          headers: {
            'Content-Type': 'application/json',
            'x-temperature-unit': temperatureUnit
          }
        });
        
        if (error) {
          console.error('Supabase invoke error:', error);
          throw error;
        }
        
        console.log('Convert recipe response:', data);

        if (data.error) {
          throw new Error(data.error);
        }

        if (!data) {
          console.error('No data received from conversion');
          setLastError("No response received");
          toast.error("No response received from the recipe converter. Please try again.");
          return;
        }

        if (!data.title) {
          console.error('Invalid recipe data received:', data);
          setLastError("No title found in recipe data");
          toast.error("We couldn't find a recipe in your input. Please try a different recipe URL or text.");
          return;
        }

        // Ensure the data matches the ConversionResult type structure
        const convertedRecipe: ConversionResult = {
          title: data.title,
          description: data.description || "",
          ingredients: data.ingredients || [],
          steps: data.steps || [],
          servings: data.servings || 1,
          cuisine_type: data.cuisine_type || null,
          food_type: data.food_type || null,
          temperature_unit: temperatureUnit as "F" | "C",
          prepTime: data.prepTime || "",
          cookTime: data.cookTime || "",
          totalTime: data.totalTime || "",
          notes: data.notes || [],
          nutrition: data.nutrition || undefined
        };

        console.log('Successfully converted recipe:', convertedRecipe);
        setRecipe(convertedRecipe);
        toast.success("Recipe imported successfully!");
      } catch (error: any) {
        console.error('Error in recipe import:', error);
        setLastError(error.message || "Unknown error occurred");
        
        // Provide more helpful error messages based on the error type
        if (error.message?.includes("service not found") || error.message?.includes("404")) {
          toast.error("The recipe conversion service isn't available yet. Please try again in a few moments.");
        } else if (error.message?.includes("Failed to fetch") || error.message?.includes("timed out")) {
          toast.error("Connection to the recipe conversion service failed. This could be due to network issues or service unavailability. Please try again later.");
        } else if (error.message?.includes("find a recipe")) {
          toast.error("We couldn't find a valid recipe in your input. Please check the URL or text and try again.");
        } else if (error.message?.includes("scrape")) {
          toast.error("We had trouble extracting the recipe from that website. Please try copying the recipe text directly.");
        } else if (error.message?.includes("token") || error.message?.includes("auth")) {
          toast.error("Authentication error. Please sign out and sign back in, then try again.");
        } else {
          toast.error("An error occurred while processing your recipe. Please try again with a different input.");
        }
      }
    } catch (error: any) {
      console.error('Error in recipe import outer handler:', error);
      setLastError(error.message || "Unknown error occurred");
      toast.error("An error occurred while processing your recipe. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setLastError(null);
  };

  return (
    <>
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-8">Create New Recipe</h1>
        
        <Tabs defaultValue="manual" className="mb-8">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            <TabsTrigger value="import">Import Recipe</TabsTrigger>
          </TabsList>

          <TabsContent value="manual">
            <CreateRecipeForm />
          </TabsContent>

          <TabsContent value="import">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <Switch
                  id="airfryer-mode"
                  checked={airfryerMode}
                  onCheckedChange={setAirfryerMode}
                />
                <div className="space-y-1">
                  <Label htmlFor="airfryer-mode" className="font-medium">
                    Air Fryer Recipe
                  </Label>
                  <p className="text-sm text-gray-500">
                    {airfryerMode 
                      ? "Convert standard recipes to work in an air fryer (adjusts temperatures and cooking times)" 
                      : "Keep original recipe settings (use this if your recipe is already air fryer-compatible)"}
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <RecipeInput 
                  onSubmit={handleRecipeImport}
                  isLoading={isLoading}
                />
              </div>

              {lastError && (
                <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>{lastError}</p>
                    <button 
                      onClick={handleRetry}
                      className="text-sm underline text-primary hover:text-primary/80 transition-colors"
                    >
                      Try again
                    </button>
                  </AlertDescription>
                </Alert>
              )}

              {recipe && (
                <div className="mt-6">
                  <CreateRecipeForm
                    initialData={recipe}
                  />
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </>
  );
};

export default CreateRecipe;
